/**
 * Desi Marketplace — R1 MVP Sample Data
 * Multi-tenant boutique storefront with assisted selling & campaign generation
 */

import type { Project, Task, Epic, Sprint } from '../types';

const ORG_ID = 'org_intellespherezamdan';
const PROJECT_ID = 'proj-desi-marketplace-r1';

export const desiMarketplaceProject: Project = {
  id: PROJECT_ID,
  organizationId: ORG_ID,
  name: 'Desi Marketplace — R1 MVP',
  description:
    'Pilot platform enabling desi boutiques to launch storefronts, capture inquiries, and generate campaign bundles.',
  status: 'In Progress',
  proposal: `# Desi Marketplace — Release 1 (R1)

## Objective
Deliver a beta that allows boutiques to launch storefronts and manage inquiries with assisted selling.

## R1 Proof Points
- Storefront live
- Inquiries captured
- Campaign bundles generated & reused
- Baseline analytics visible

## Out of Scope
- Marketplace discovery
- Platform checkout/payments
- Logistics & fulfillment
`,
  costs: [
    { id: 'cost-1', description: 'Cloud Hosting', amount: 24000, category: 'Infrastructure' },
    { id: 'cost-2', description: 'Development Team (3 months)', amount: 180000, category: 'Personnel' },
    { id: 'cost-3', description: 'AI API Usage', amount: 12000, category: 'Services' },
  ],
  teamMembers: [
    { userId: 'aidimentry', role: 'developer' },
    { userId: 'jesin.shah', role: 'owner' },
    { userId: 'asif.redmond', role: 'designer' },
  ],
  createdAt: '2026-01-01T00:00:00Z',
  updatedAt: '2026-03-01T00:00:00Z',
  startDate: '2026-03-01',
  targetEndDate: '2026-05-15',
  costPerHour: 75,
  externalLinks: [{ type: 'GitHub', url: 'https://github.com/desi-marketplace/platform' }],
};

//
// EPICS
//

export const desiMarketplaceEpics: Epic[] = [
  {
    id: 'epic-onboarding',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Boutique Onboarding & Roles',
    description: 'Signup, workspace creation, boutique profile & roles',
    status: 'In Progress',
    taskIds: [],
    standupNotes: [],
    costPerHour: 75,
    createdAt: '2026-03-01T00:00:00Z',
    updatedAt: '2026-03-01T00:00:00Z',
  },
  {
    id: 'epic-storefront',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Storefront Launcher',
    description: 'Hosted storefront, PDP, measurements, inquiry capture',
    status: 'In Progress',
    taskIds: [],
    standupNotes: [],
    costPerHour: 75,
    createdAt: '2026-03-01T00:00:00Z',
    updatedAt: '2026-03-01T00:00:00Z',
  },
  {
    id: 'epic-assisted-selling',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Assisted Selling',
    description: 'Inquiry inbox and AI response suggestions',
    status: 'Not Started',
    taskIds: [],
    standupNotes: [],
    costPerHour: 75,
    createdAt: '2026-03-01T00:00:00Z',
    updatedAt: '2026-03-01T00:00:00Z',
  },
  {
    id: 'epic-campaigns',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Campaign Studio',
    description: 'Campaign bundle generation & reuse',
    status: 'Not Started',
    taskIds: [],
    standupNotes: [],
    costPerHour: 75,
    createdAt: '2026-03-01T00:00:00Z',
    updatedAt: '2026-03-01T00:00:00Z',
  },
  {
    id: 'epic-analytics',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Analytics & Metrics',
    description: 'Storefront events and inquiry funnel metrics',
    status: 'Not Started',
    taskIds: [],
    standupNotes: [],
    costPerHour: 75,
    createdAt: '2026-03-01T00:00:00Z',
    updatedAt: '2026-03-01T00:00:00Z',
  },
  {
    id: 'epic-admin',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Admin & Reliability',
    description: 'Admin console navigation and platform stability',
    status: 'Not Started',
    taskIds: [],
    standupNotes: [],
    costPerHour: 75,
    createdAt: '2026-03-01T00:00:00Z',
    updatedAt: '2026-03-01T00:00:00Z',
  },
];

//
// TASKS (P0 CUT LINE)
//

export const desiMarketplaceTasks: Task[] = [
  {
    id: 'A1',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Boutique Signup & Workspace Creation',
    description: 'Email/password signup and workspace provisioning',
    type: 'Feature',
    status: 'In Progress',
    priority: 'P0',
    complexity: 'Medium',
    category: 'Auth',
    assigneeIds: ['user-dev-1'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-onboarding',
    estimatedDays: 3,
    estimatedHours: 24,
    dependencies: '',
    skillsRequired: 'Firebase Auth',
    phase: '1',
    deliverables: 'Signup flow',
    notes: '',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-03-01T00:00:00Z',
    updatedAt: '2026-03-01T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'A2',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Boutique Profile & Channels Setup',
    description: 'Configure boutique info and contact channels',
    type: 'Feature',
    status: 'Not Started',
    priority: 'P0',
    complexity: 'Low',
    category: 'Settings',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-onboarding',
    estimatedDays: 2,
    estimatedHours: 16,
    dependencies: 'A1',
    skillsRequired: 'React',
    phase: '1',
    deliverables: 'Profile form',
    notes: '',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-03-01T00:00:00Z',
    updatedAt: '2026-03-01T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'B1',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Hosted Storefront & Boutique URL',
    description: 'Mobile-first storefront with unique URL',
    type: 'Feature',
    status: 'In Progress',
    priority: 'P0',
    complexity: 'High',
    category: 'Frontend',
    assigneeIds: ['user-dev-1'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-storefront',
    estimatedDays: 5,
    estimatedHours: 40,
    dependencies: 'A1',
    skillsRequired: 'React Routing',
    phase: '1',
    deliverables: 'Home, PDP routes',
    notes: '',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-03-01T00:00:00Z',
    updatedAt: '2026-03-01T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'B5',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Inquiry Capture on PDP',
    description: 'Inquiry form tied to product',
    type: 'Feature',
    status: 'Not Started',
    priority: 'P0',
    complexity: 'Medium',
    category: 'Messaging',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-storefront',
    estimatedDays: 3,
    estimatedHours: 24,
    dependencies: 'B1',
    skillsRequired: 'Firestore',
    phase: '1',
    deliverables: 'Inquiry form',
    notes: '',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-03-01T00:00:00Z',
    updatedAt: '2026-03-01T00:00:00Z',
    customerVisible: true,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'C1',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Inquiry Inbox & Status',
    description: 'Admin inbox with New/Responded/Closed states',
    type: 'Feature',
    status: 'Not Started',
    priority: 'P0',
    complexity: 'Medium',
    category: 'Admin',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-assisted-selling',
    estimatedDays: 4,
    estimatedHours: 32,
    dependencies: 'B5',
    skillsRequired: 'React',
    phase: '1',
    deliverables: 'Inbox UI',
    notes: '',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-03-01T00:00:00Z',
    updatedAt: '2026-03-01T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'D1',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Campaign Bundle Generator',
    description: 'Generate IG captions, WhatsApp broadcasts, DM scripts',
    type: 'Feature',
    status: 'Not Started',
    priority: 'P0',
    complexity: 'High',
    category: 'AI',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-campaigns',
    estimatedDays: 6,
    estimatedHours: 48,
    dependencies: 'B1',
    skillsRequired: 'LLM Integration',
    phase: '1',
    deliverables: 'Campaign generator',
    notes: '',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-03-01T00:00:00Z',
    updatedAt: '2026-03-01T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'E1',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Storefront Events Tracking',
    description: 'Track page views, product views, CTA clicks',
    type: 'Feature',
    status: 'Not Started',
    priority: 'P0',
    complexity: 'Medium',
    category: 'Analytics',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-analytics',
    estimatedDays: 3,
    estimatedHours: 24,
    dependencies: 'B1',
    skillsRequired: 'Analytics',
    phase: '1',
    deliverables: 'Event logging',
    notes: '',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-03-01T00:00:00Z',
    updatedAt: '2026-03-01T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'F1',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Admin Console Navigation',
    description: 'Products, Inquiries, Campaigns, Settings, Analytics',
    type: 'Feature',
    status: 'In Progress',
    priority: 'P0',
    complexity: 'Medium',
    category: 'Admin',
    assigneeIds: ['user-dev-1'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-admin',
    estimatedDays: 4,
    estimatedHours: 32,
    dependencies: '',
    skillsRequired: 'React',
    phase: '1',
    deliverables: 'Admin navigation',
    notes: '',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-03-01T00:00:00Z',
    updatedAt: '2026-03-01T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
];

//
// SPRINT
//
export const desiMarketplaceSprint: Sprint = {
  id: 'sprint-r1-core',
  organizationId: ORG_ID,
  projectId: PROJECT_ID,
  name: 'Sprint R1 — Core MVP',
  startDate: '2026-03-01',
  endDate: '2026-03-21',
  status: 'Active',
  taskIds: ['A1', 'A2', 'B1', 'B5', 'C1', 'D1', 'E1', 'F1'],
  goals:
    'Launch storefront, capture inquiries, generate campaigns, and show baseline analytics for pilot boutiques.',
};
